import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useLocation, useNavigate } from 'react-router-dom';
import { useQueryClient } from '@tanstack/react-query';
import {
  GraduationCap,
  ChevronRight,
  CheckCircle2,
  X,
} from 'lucide-react';

import { Popover, PopoverContent, PopoverTrigger } from '../ui/popover';
import { Button } from '../ui/button';
import { settingsApi } from '../../services/api';
import { useSettingsQuery } from '../../hooks/queries';
import { settingsKeys } from '../../lib/queryKeys';
import {
  sectionsFor,
  sectionProgress,
  overallProgress,
  isAllComplete,
  stepMeta,
} from '../../data/tutorials';
import SpotlightOverlay, { useTargetRect } from './SpotlightOverlay';
import StepVisual from './StepVisual';
import { EMPTY_OBJECT } from '../../lib/empty';

// Circular SVG ring drawn concentric with the trigger icon. Uses viewBox so the
// SVG scales with its parent button and uses pathLength=100 so the dasharray
// math reads cleanly as percentages rather than computed circumferences.
const ProgressRing = ({ ratio, stroke = 1.5 }) => {
  const offset = 100 * (1 - Math.max(0, Math.min(1, ratio)));
  return (
    <svg
      viewBox="0 0 36 36"
      className="absolute inset-0 h-full w-full -rotate-90 pointer-events-none"
      aria-hidden="true"
    >
      <circle
        cx="18"
        cy="18"
        r="16"
        fill="none"
        stroke="currentColor"
        strokeWidth={stroke}
        className="text-border/40"
      />
      <circle
        cx="18"
        cy="18"
        r="16"
        fill="none"
        stroke="currentColor"
        strokeWidth={stroke}
        strokeLinecap="round"
        pathLength="100"
        strokeDasharray="100"
        strokeDashoffset={offset}
        className="text-amber-500 transition-[stroke-dashoffset] duration-300"
      />
    </svg>
  );
};

/* ---------------------------- Coachmark runner ---------------------------- */
// Walks the user through a section step-by-step. For steps with a `target` CSS
// selector the runner navigates (if `route` is set), waits for the element to
// appear, and dims the rest of the page with a spotlight around it. Steps with
// no target render a centered card so concept-only lessons still work.
const CoachmarkRunner = ({ section, startIndex, onClose, onComplete, completedStepIds }) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  const [index, setIndex] = useState(startIndex);
  const [advancing, setAdvancing] = useState(false);
  // Ref guard: unlike state, it's synchronous — a second click landing in the
  // same tick (before re-render) still sees `true` and bails out. Without this,
  // rapid Next clicks each queue `setIndex(i => i + 1)` after their async
  // persist resolves, pushing the index past the last step and rendering
  // undefined localization keys (BG_56).
  const advancingRef = useRef(false);
  const stepId = section.steps[index];
  const total = section.steps.length;
  const isLast = index === total - 1;
  const isCompleted = completedStepIds.includes(stepId);
  const meta = stepMeta(section.id, stepId);

  // Navigate to the step's preferred route. We compare the full pathname+search
  // so routes that carry a query (e.g. ?tab=integrations on Settings) actually
  // land on the right tab — pathname-only would have matched and skipped nav.
  // Only when the step changes: the person may move on to another page during a step.
  const locationRef = useRef(location);
  locationRef.current = location;
  const stepRoute = meta.route;
  useEffect(() => {
    if (!stepRoute) return;
    const { pathname, search } = locationRef.current;
    if (`${pathname}${search}` !== stepRoute) {
      navigate(stepRoute);
    }
  }, [stepId, stepRoute, navigate]);

  // The Getting Started section needs the sidebar visible for its sidebar/search
  // steps. If the user previously collapsed it, force-expand for the duration
  // of the tour so the spotlights actually find their targets.
  useEffect(() => {
    if (section.id === 'getting_started') {
      window.dispatchEvent(
        new CustomEvent('sidebarCollapseChanged', { detail: { value: 0 } }),
      );
    }
  }, [section.id, stepId]);

  // Wait for the target element. The `index` dep restarts polling each step so
  // a previously-found target from the prior step doesn't leak into this one.
  const rect = useTargetRect(meta.target, true, `${index}|${location.pathname}`);

  const markAndAdvance = async () => {
    if (advancingRef.current) return;
    advancingRef.current = true;
    setAdvancing(true);
    try {
      if (!isCompleted) await onComplete(section.id, stepId);
      if (!isLast) setIndex((i) => Math.min(i + 1, total - 1));
      else onClose();
    } finally {
      advancingRef.current = false;
      setAdvancing(false);
    }
  };
  const prev = () => setIndex((i) => Math.max(0, i - 1));

  // Centered card placement when no target is configured for this step.
  const placement = meta.target ? meta.placement : 'bottom';
  const effectiveRect = meta.target ? rect : null;

  return (
    <SpotlightOverlay
      rect={effectiveRect}
      placement={placement}
      onDismiss={onClose}
      dismissable={false}
      // Concept-only steps (no target) render as a floating tip in the bottom-
      // right without dimming so the user can see the page they were routed to.
      dim={!!effectiveRect}
    >
      <div className="flex items-center justify-between border-b px-4 py-2.5">
        <div className="flex min-w-0 items-center gap-2 text-xs">
          <span className="truncate font-medium">
            {t(`tutorials.sections.${section.id}.title`)}
          </span>
          <span className="shrink-0 text-muted-foreground">
            {t('tutorials.step.of', { current: index + 1, total })}
          </span>
        </div>
        <button
          type="button"
          onClick={onClose}
          className="shrink-0 text-muted-foreground hover:text-foreground transition-colors"
          aria-label={t('tutorials.step.close')}
        >
          <X className="h-3.5 w-3.5" />
        </button>
      </div>

      <div className="px-4 py-3.5">
        <h2 className="text-sm font-semibold tracking-tight">
          {t(`tutorials.sections.${section.id}.steps.${stepId}.title`)}
        </h2>
        <p className="mt-1.5 text-xs leading-relaxed text-muted-foreground">
          {t(`tutorials.sections.${section.id}.steps.${stepId}.body`)}
        </p>
        <StepVisual slug={meta.visual} />
      </div>

      <div className="flex items-center justify-between gap-2 border-t px-4 py-2.5">
        <Button
          variant="ghost"
          size="sm"
          onClick={prev}
          disabled={index === 0}
          className="h-7 text-xs"
        >
          {t('tutorials.step.prev')}
        </Button>
        <div className="flex items-center gap-1">
          {Array.from({ length: total }).map((_, i) => (
            <span
              key={i}
              className={`h-1 rounded-full transition-all ${
                i === index ? 'w-4 bg-foreground' : 'w-1 bg-muted-foreground/30'
              }`}
            />
          ))}
        </div>
        <Button size="sm" onClick={markAndAdvance} disabled={advancing} className="h-7 text-xs">
          {isLast ? t('tutorials.step.done') : t('tutorials.step.next')}
        </Button>
      </div>
    </SpotlightOverlay>
  );
};

/* ------------------------------ Section list ----------------------------- */
const SectionRow = ({ section, progressMap, onStart }) => {
  const { t } = useTranslation();
  const { done, total, ratio } = sectionProgress(section, progressMap);
  const Icon = section.icon;
  const completed = done >= total;
  const inProgress = done > 0 && !completed;

  const label = completed
    ? t('tutorials.popover.cta_replay')
    : inProgress
      ? t('tutorials.popover.cta_continue')
      : t('tutorials.popover.cta_start');

  return (
    <div className="group flex items-center gap-3 rounded-md px-2 py-2 hover:bg-muted/60">
      <div className="flex h-8 w-8 shrink-0 items-center justify-center rounded-md bg-muted text-muted-foreground">
        <Icon className="h-4 w-4" />
      </div>
      <div className="min-w-0 flex-1">
        <div className="flex items-center gap-2">
          <span className="truncate text-sm font-medium">
            {t(`tutorials.sections.${section.id}.title`)}
          </span>
          {completed && <CheckCircle2 className="h-3.5 w-3.5 shrink-0 text-emerald-500" />}
        </div>
        <div className="truncate text-xs text-muted-foreground">
          {t(`tutorials.sections.${section.id}.description`)}
        </div>
        <div className="mt-1.5 flex items-center gap-2">
          <div className="h-1 flex-1 overflow-hidden rounded-full bg-muted-foreground/15">
            <div
              className="h-full rounded-full bg-amber-500 transition-[width] duration-300"
              style={{ width: `${Math.round(ratio * 100)}%` }}
            />
          </div>
          <span className="shrink-0 text-[10px] tabular-nums text-muted-foreground">
            {done}/{total}
          </span>
        </div>
      </div>
      <button
        type="button"
        onClick={onStart}
        className="inline-flex shrink-0 items-center gap-1 rounded-md px-2 py-1 text-xs font-medium text-foreground hover:bg-accent transition-colors"
      >
        {label}
        <ChevronRight className="h-3 w-3" />
      </button>
    </div>
  );
};

/* ------------------------------- Trigger ------------------------------- */
const TutorialCenter = ({ side = 'bottom', align = 'end' } = {}) => {
  const { t } = useTranslation();
  const settingsQuery = useSettingsQuery();
  const queryClient = useQueryClient();
  const [open, setOpen] = useState(false);
  // Active section the user picked to walk through; null when popover is the focus.
  const [activeSection, setActiveSection] = useState(null);
  const [startIndex, setStartIndex] = useState(0);

  const settings = settingsQuery.data;
  const progressMap = settings?.tutorial_progress || EMPTY_OBJECT;
  const overall = useMemo(() => overallProgress(progressMap), [progressMap]);
  const sections = useMemo(() => sectionsFor(settings?.persona), [settings?.persona]);
  const allDone = isAllComplete(progressMap);
  const onboardingDone = settings?.onboarding_completed === true;

  // Hide entirely if everything completed - icon disappears from header per spec.
  // Also hide while settings haven't loaded, and during onboarding modal flow.
  if (!settings || allDone || !onboardingDone) return null;

  const persistCompletion = async (sectionId, stepId) => {
    const existing = (progressMap[sectionId] || []).filter((id) => id !== stepId);
    const next = [...existing, stepId];
    const update = { ...progressMap, [sectionId]: next };
    try {
      await settingsApi.update({ tutorial_progress: update });
      await queryClient.invalidateQueries({ queryKey: settingsKeys.current() });
    } catch (e) {
      console.error('Failed to save tutorial progress', e);
    }
  };

  const resetSection = async (sectionId) => {
    const update = { ...progressMap };
    delete update[sectionId];
    try {
      await settingsApi.update({ tutorial_progress: update });
      await queryClient.invalidateQueries({ queryKey: settingsKeys.current() });
    } catch (e) {
      console.error('Failed to reset section', e);
    }
  };

  const startSection = (section) => {
    const done = progressMap[section.id] || [];
    const firstUnfinished = section.steps.findIndex((id) => !done.includes(id));
    setStartIndex(firstUnfinished >= 0 ? firstUnfinished : 0);
    setActiveSection(section);
    setOpen(false);
  };

  const replaySection = async (section) => {
    await resetSection(section.id);
    setStartIndex(0);
    setActiveSection(section);
    setOpen(false);
  };

  return (
    <>
      <Popover open={open} onOpenChange={setOpen}>
        <PopoverTrigger asChild>
          <button
            type="button"
            data-tutorial-trigger="true"
            title={t('tutorials.trigger.title')}
            aria-label={t('tutorials.trigger.title')}
            className="relative hidden md:inline-flex h-8 w-8 items-center justify-center rounded-full text-muted-foreground hover:text-foreground transition-colors"
          >
            <ProgressRing ratio={overall.ratio} />
            <GraduationCap className="h-4 w-4" />
            <span className="sr-only">{t('tutorials.trigger.title')}</span>
          </button>
        </PopoverTrigger>
        <PopoverContent
          side={side}
          align={align}
          sideOffset={8}
          className="w-96 max-w-[calc(100vw-1rem)] p-0"
        >
          <div className="border-b px-4 py-3">
            <div className="flex items-center justify-between">
              <h3 className="text-sm font-semibold">{t('tutorials.popover.title')}</h3>
              <span className="text-xs tabular-nums text-muted-foreground">
                {t('tutorials.popover.progress', { done: overall.done, total: overall.total })}
              </span>
            </div>
            <div className="mt-2 h-1.5 overflow-hidden rounded-full bg-muted">
              <div
                className="h-full rounded-full bg-amber-500 transition-[width] duration-300"
                style={{ width: `${Math.round(overall.ratio * 100)}%` }}
              />
            </div>
          </div>
          <div className="max-h-[60vh] overflow-y-auto py-2">
            {sections.map((section) => {
              const { done, total } = sectionProgress(section, progressMap);
              const completed = done >= total;
              return (
                <SectionRow
                  key={section.id}
                  section={section}
                  progressMap={progressMap}
                  onStart={() => (completed ? replaySection(section) : startSection(section))}
                />
              );
            })}
          </div>
        </PopoverContent>
      </Popover>

      {activeSection && (
        <CoachmarkRunner
          section={activeSection}
          startIndex={startIndex}
          completedStepIds={progressMap[activeSection.id] || []}
          onClose={() => setActiveSection(null)}
          onComplete={persistCompletion}
        />
      )}
    </>
  );
};

export default TutorialCenter;
